import {
  Glass,
  PillChip,
  Dot,
  DisplayText,
  BodyText,
  alpha,
  borderAlpha,
} from "../../components/mobile/primitives";

// state: "results" | "minor-update" | "stale" | "needs-data" | "ready" | "running"
export const SKILL_AUTHORS = [
  {
    name: "Dr. Sara Chen",
    initials: "SC",
    accent: "var(--color-kin-lavender)",
    role: "Sleep and recovery",
    skills: [
      {
        accent: "var(--color-kin-lavender)",
        title: "Sleep Deep Dive",
        sub: "8 new sleep nights and a lipid panel since the last run",
        badge: "+8 nights, +1 lab",
        badgeColor: "var(--color-kin-lavender)",
        state: "minor-update",
        updated: "2h ago",
        primary: { label: "Re-run", filled: true },
        secondary: { label: "View last" },
      },
      {
        accent: "var(--color-kin-green-soft)",
        title: "Circadian Reset",
        sub: "This report is from Feb 2 — 45 new nights the skill hasn't seen",
        badge: "Stale \u2014 45 nights",
        badgeColor: "var(--color-kin-orange)",
        state: "stale",
        updated: "47 days ago",
        primary: { label: "Run fresh", filled: true },
        secondary: { label: "View old" },
      },
      {
        accent: "var(--color-kin-orange)",
        title: "Stress Load Check",
        sub: "HRV drops vs workload markers",
        badge: "Ready to run",
        badgeColor: "var(--color-kin-muted)",
        state: "ready",
        updated: null,
        primary: { label: "Run", filled: true },
        secondary: null,
      },
    ],
  },
  {
    name: "Jonas Weber",
    initials: "JW",
    accent: "var(--color-kin-green-soft)",
    role: "Labs and metabolic",
    skills: [
      {
        accent: "var(--color-kin-green-soft)",
        title: "Lab Panel Review",
        sub: "Results ready \u2014 run completed 2h ago",
        badge: "Results ready",
        badgeColor: "var(--color-kin-green)",
        state: "results",
        updated: "2h ago",
        primary: { label: "View", filled: true },
        secondary: { label: "Chat" },
      },
      {
        accent: "var(--color-kin-gold)",
        title: "Nutrition Audit",
        sub: "Found 3 days of meals, needs 7+",
        badge: "Needs 4 more days",
        badgeColor: "var(--color-kin-orange)",
        state: "needs-data",
        updated: null,
        primary: { label: "Set up", filled: false },
        secondary: null,
      },
    ],
  },
];

export function SkillsScreen() {
  return (
    <div style={{ padding: "8px 16px 0", display: "flex", flexDirection: "column", gap: 14 }}>
      <DisplayText
        size={28}
        color="var(--color-kin-black)"
        lineHeight={1.1}
        style={{ paddingLeft: 4 }}
      >
        Skills
      </DisplayText>

      {/* Category chips */}
      <div
        className="phone-scroll"
        style={{
          display: "flex",
          gap: 6,
          flexWrap: "nowrap",
          overflowX: "auto",
          paddingLeft: 4,
          paddingBottom: 4,
        }}
      >
        {[
          ["All", true],
          ["Sleep", false],
          ["Lab", false],
          ["Nutrition", false],
          ["Cardio", false],
        ].map(([label, active]) => (
          <PillChip key={label} active={active} color="var(--color-kin-green-soft)">
            {label}
          </PillChip>
        ))}
      </div>

      {/* Tracker required by a skill */}
      <div
        style={{
          background: "rgba(255,208,40,0.1)",
          border: "1px solid rgba(255,208,40,0.3)",
          borderRadius: 20,
          padding: "12px 14px",
          display: "flex",
          alignItems: "center",
          gap: 12,
          boxShadow: "0 4px 12px -4px rgba(255,208,40,0.15)",
        }}
      >
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: 14,
            background: "rgba(255,208,40,0.2)",
            border: "1px solid rgba(255,208,40,0.4)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            boxShadow: "0 0 8px rgba(255,208,40,0.3)",
          }}
        >
          <BodyText size={10} color="#f0b800" weight={800}>
            i
          </BodyText>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <BodyText size={10} color="#f0b800" weight={800} style={{ marginBottom: 2 }}>
            Nutrition Audit needs more data
          </BodyText>
          <BodyText size={9} color="#7e8b82" weight={500} style={{ lineHeight: 1.4 }}>
            Tried to read meal logs — found 3 days, needs 7+. Log 4 more days to unlock.
          </BodyText>
        </div>
        <div
          style={{
            padding: "6px 12px",
            borderRadius: 24,
            flexShrink: 0,
            background: "rgba(255,208,40,0.2)",
            border: "1px solid rgba(255,208,40,0.4)",
          }}
        >
          <BodyText size={9} color="#f0b800" weight={800}>
            Set up
          </BodyText>
        </div>
      </div>

      {/* Author collections */}
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {SKILL_AUTHORS.map((author) => (
          <Glass key={author.name} style={{ padding: "14px 14px", borderRadius: 24 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 8,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10, flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 16,
                    background: alpha(author.accent, "20"),
                    border: borderAlpha(author.accent, "50"),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    boxShadow: `0 0 10px ${alpha(author.accent, "40")}`,
                  }}
                >
                  <BodyText
                    size={10}
                    color={author.accent}
                    weight={800}
                    style={{ letterSpacing: "0.05em" }}
                  >
                    {author.initials}
                  </BodyText>
                </div>
                <div style={{ minWidth: 0 }}>
                  <BodyText size={11} weight={800} color="var(--color-kin-black)">
                    <span
                      style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        display: "block",
                      }}
                    >
                      {author.name}
                    </span>
                  </BodyText>
                  <BodyText
                    size={9}
                    color="var(--color-kin-muted)"
                    weight={600}
                    style={{ marginTop: 1 }}
                  >
                    {author.role}
                  </BodyText>
                </div>
              </div>
              <div
                style={{
                  padding: "4px 10px",
                  borderRadius: 14,
                  background: "rgba(255,255,255,0.7)",
                  border: "1px solid rgba(0,0,0,0.05)",
                  fontSize: 8.5,
                  fontWeight: 800,
                  color: "var(--color-kin-muted)",
                  fontFamily: "'Manrope', system-ui",
                  flexShrink: 0,
                  boxShadow: "0 2px 6px -2px rgba(0,0,0,0.05)",
                }}
              >
                {author.skills.length} skills
              </div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 12 }}>
              {author.skills.map((skill) => (
                <div
                  key={skill.title}
                  style={{
                    padding: "10px 12px",
                    borderRadius: 16,
                    background: "rgba(254,253,251,0.6)",
                    border: "1px solid rgba(255,255,255,0.8)",
                    boxShadow: "0 2px 8px -2px rgba(0,0,0,0.03)",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                    <Dot
                      color={skill.accent}
                      size={8}
                      style={{ marginTop: 4, boxShadow: `0 0 6px ${alpha(skill.accent, "80")}` }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <BodyText size={11} weight={800} color="var(--color-kin-black)">
                        <span
                          style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            display: "block",
                          }}
                        >
                          {skill.title}
                        </span>
                      </BodyText>
                      <BodyText
                        size={9}
                        color="var(--color-kin-muted)"
                        weight={500}
                        style={{ marginTop: 2 }}
                      >
                        {skill.sub}
                      </BodyText>
                    </div>
                    <div
                      style={{
                        padding: "3px 8px",
                        borderRadius: 10,
                        background: alpha(skill.badgeColor, "15"),
                        border: borderAlpha(skill.badgeColor, "30"),
                        fontSize: 8,
                        fontWeight: 800,
                        color: skill.badgeColor,
                        fontFamily: "'Manrope', system-ui",
                        flexShrink: 0,
                      }}
                    >
                      {skill.badge}
                    </div>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      marginTop: 10,
                    }}
                  >
                    {skill.updated ? (
                      <BodyText size={8.5} color="#7e8b82" weight={600}>
                        Updated {skill.updated}
                      </BodyText>
                    ) : (
                      <div />
                    )}
                    <div
                      style={{
                        display: "flex",
                        background: "rgba(255,255,255,0.8)",
                        borderRadius: 14,
                        overflow: "hidden",
                        border: "1px solid rgba(0,0,0,0.04)",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
                      }}
                    >
                      <div
                        style={{
                          padding: "5px 10px",
                          borderRight: skill.secondary ? "1px solid rgba(0,0,0,0.04)" : "none",
                          background: skill.primary.filled ? "#1b1d1a" : "transparent",
                        }}
                      >
                        <BodyText
                          size={8.5}
                          color={skill.primary.filled ? "#ffffff" : skill.accent}
                          weight={800}
                        >
                          {skill.primary.label}
                        </BodyText>
                      </div>
                      {skill.secondary && (
                        <div
                          style={{
                            padding: "5px 10px",
                            background: "rgba(254,253,251,0.5)",
                          }}
                        >
                          <BodyText size={8.5} color={skill.accent} weight={800}>
                            {skill.secondary.label}
                          </BodyText>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Glass>
        ))}
      </div>
    </div>
  );
}
